import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { DesempenhoPrestador } from '@/hooks/useDashboard';

interface TabelaDesempenhoPrestadoresProps {
  dados: DesempenhoPrestador[];
}

type SortKey = 'nome' | 'osAtribuidas' | 'osConcluidas' | 'osRecusadas' | 'taxaConclusao';

/**
 * Tier monocromático: distinção por nível de cinza, sem cor.
 * - excellent (≥80%): ink invert (mais escuro)
 * - good (60-79%):    default cinza
 * - regular (<60%):   soft cinza claro
 */
function getDesempenhoTier(taxa: number): 'ink' | 'default' | 'soft' {
  if (taxa >= 80) return 'ink';
  if (taxa >= 60) return 'default';
  return 'soft';
}

function getDesempenhoKey(taxa: number): string {
  if (taxa >= 80) return 'excellent';
  if (taxa >= 60) return 'good';
  return 'regular';
}

export function TabelaDesempenhoPrestadores({ dados }: TabelaDesempenhoPrestadoresProps) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState<SortKey>('osAtribuidas');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  if (!dados || dados.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        {t('dashboard.tables.noProviders')}
      </div>
    );
  }

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };

  const dadosOrdenados = [...dados].sort((a, b) => {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    if (sortKey === 'nome') return multiplier * a.nome.localeCompare(b.nome);
    return multiplier * (a[sortKey] - b[sortKey]);
  });

  // Identificar melhor prestador (maior taxa com pelo menos 1 OS concluída)
  const melhorPrestador = dados.reduce(
    (melhor, atual) => {
      if (atual.osConcluidas === 0) return melhor;
      if (!melhor || atual.taxaConclusao > melhor.taxaConclusao) return atual;
      return melhor;
    },
    null as DesempenhoPrestador | null,
  );

  const sortArrow = (k: SortKey) => (sortKey === k ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : '');

  return (
    <div className="flex flex-col gap-3">
      {melhorPrestador && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40 border border-border-soft">
          <Trophy className="h-4 w-4 text-foreground" />
          <span className="text-sm">
            {t('dashboard.tables.provider')}:{' '}
            <strong>{melhorPrestador.nome}</strong> ({melhorPrestador.taxaConclusao}%{' '}
            {t('dashboard.tables.completionRate').toLowerCase()})
          </span>
        </div>
      )}

      <div className="tbl-card">
        <table className="tbl">
          <thead>
            <tr>
              <th onClick={() => handleSort('nome')} style={{ cursor: 'pointer' }}>
                {t('dashboard.tables.provider')}{sortArrow('nome')}
              </th>
              <th
                className="num"
                onClick={() => handleSort('osAtribuidas')}
                style={{ cursor: 'pointer' }}
              >
                {t('dashboard.tables.assigned')}{sortArrow('osAtribuidas')}
              </th>
              <th
                className="num"
                onClick={() => handleSort('osConcluidas')}
                style={{ cursor: 'pointer' }}
              >
                {t('dashboard.tables.completed')}{sortArrow('osConcluidas')}
              </th>
              <th
                className="num"
                onClick={() => handleSort('osRecusadas')}
                style={{ cursor: 'pointer' }}
              >
                {t('dashboard.tables.refused')}{sortArrow('osRecusadas')}
              </th>
              <th
                className="num"
                onClick={() => handleSort('taxaConclusao')}
                style={{ cursor: 'pointer' }}
              >
                {t('dashboard.tables.completionRate')}{sortArrow('taxaConclusao')}
              </th>
              <th>{t('dashboard.tables.performance')}</th>
            </tr>
          </thead>
          <tbody>
            {dadosOrdenados.map((prestador) => {
              const tier = getDesempenhoTier(prestador.taxaConclusao);
              const label = t(`dashboard.tables.${getDesempenhoKey(prestador.taxaConclusao)}`);
              const isMelhor = melhorPrestador?.id === prestador.id;
              const pillVariant =
                tier === 'ink'
                  ? 'status-pill--ink'
                  : tier === 'soft'
                    ? 'status-pill--soft'
                    : '';

              return (
                <tr key={prestador.id} style={{ cursor: 'default' }}>
                  <td>
                    <div className="flex items-center gap-2">
                      {isMelhor && (
                        <Trophy className="h-3.5 w-3.5 text-foreground flex-shrink-0" />
                      )}
                      <span className="font-medium">{prestador.nome}</span>
                    </div>
                  </td>
                  <td className="num mono">{prestador.osAtribuidas}</td>
                  <td className="num mono">{prestador.osConcluidas}</td>
                  <td className="num mono">{prestador.osRecusadas}</td>
                  <td className="num t-currency">{prestador.taxaConclusao}%</td>
                  <td>
                    <span className={`status-pill ${pillVariant}`}>{label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legenda monocromática */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground pt-1">
        <span className="inline-flex items-center gap-1.5">
          <span className="status-pill status-pill--ink" style={{ padding: '1px 7px', fontSize: 9 }}>
            {t('dashboard.tables.excellent')}
          </span>
          ≥ 80%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="status-pill" style={{ padding: '1px 7px', fontSize: 9 }}>
            {t('dashboard.tables.good')}
          </span>
          ≥ 60%
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="status-pill status-pill--soft" style={{ padding: '1px 7px', fontSize: 9 }}>
            {t('dashboard.tables.regular')}
          </span>
          &lt; 60%
        </span>
      </div>
    </div>
  );
}
